import SettingsIcon from "@mui/icons-material/Settings";
import { Button, Chip, Tooltip } from "@mui/material";
import React, { useState } from "react";
import {
  Datagrid,
  DateField,
  FilterButton,
  FunctionField,
  Link,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TopToolbar,
  useRecordContext,
  usePermissions,
} from "react-admin";

import FeatureIntro from "../../components/FeatureIntro";
import { CPFField } from "../../components/Fields";
import ListPagination from "../../components/ListPagination";
import { PERMISSIONS } from "../../constants";
import NegativeBalanceAlertConfigDialog from "./NegativeBalanceAlertConfigDialog";

/**
 * Ocorrências de saldo negativo. O caso é gravado em tempo real pelo helper
 * `registerNegativeBalance`, chamado logo depois de cada gravação de transação negativa
 * (backend, sales-api, socket-server e sync) — NÃO há trigger no banco, o que existiu foi
 * revertido antes de subir. As colunas de notificação são do NegativeBalanceAlertWorker,
 * que também roda a varredura diária (`detectedBy = 'sweep'`).
 *
 * O estorno esperado (cancelamento de venda sobre saldo positivo) fica FORA da lista por
 * default: é o mecanismo normal, não falha, e o endpoint esconde quando o filtro "Tipo de
 * caso" não está em "todos". A linha continua gravada.
 */

const CustomerWithLink = (props) => {
  const record = useRecordContext();
  if (!record || !record.customer) { return <CPFField {...props} />; }

  return (
    <Link to={`/customers/${record.customer.id}/show/transactions`}>
      <CPFField {...props} />
    </Link>
  );
};

// `props` recebe label/textAlign repassados pelo Datagrid, como no CustomerWithLink.
const StatusField = (props: any) => {
  const record = useRecordContext();
  if (!record) { return null; }

  return record.isResolved
    ? <Chip size="small" color="success" variant="outlined" label="Resolvido" />
    : <Chip size="small" color="error" label="Em aberto" />;
};

/**
 * Estado da notificação. `sending` significa que o envio foi reservado mas o
 * resultado não voltou — normalmente só aparece por instantes.
 *
 * O canal sai do `emailTo`, que é o destino gravado. Linha antiga tem endereço de
 * verdade e cai no ramo de e-mail sozinha.
 */
const NotificationField = (props: any) => {
  const record = useRecordContext();
  if (!record) { return null; }

  if (!record.isNotified) {
    return <Chip size="small" variant="outlined" label="Não notificado" />;
  }

  const isDiscord = record.emailTo === 'discord';
  const channel = isDiscord ? '💬 Discord' : '✉️ E-mail';

  if (record.emailStatus === 'error') {
    return (
      <Tooltip title={record.emailError || 'Falha no envio'}>
        <Chip size="small" color="warning" label={`${channel}: falhou`} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isDiscord ? 'Enviado no webhook do Discord' : record.emailTo || ''}>
      <Chip size="small" color="info" variant="outlined" label={channel} />
    </Tooltip>
  );
};

/**
 * O lançamento que derrubou o saldo. Marca o estorno esperado, que só aparece aqui quando
 * o filtro "Tipo de caso" está em "todos" — sem a marca não daria para saber qual linha
 * da lista é o mecanismo normal e qual é caso para olhar.
 */
const TransactionField = (props: any) => {
  const record = useRecordContext();
  if (!record) { return null; }
  if (!record.triggerTransactionType) { return <span>—</span>; }

  const value = record.triggerTransactionValue;
  const label = value === null || value === undefined
    ? record.triggerTransactionType
    : `${record.triggerTransactionType} (${Number(value).toFixed(2)})`;

  if (!record.isExpectedReversal) { return <span>{label}</span>; }

  return (
    <Tooltip title="O cliente estava positivo e o estorno do cancelamento derrubou o saldo: é como o estorno funciona, não falha. Não gera aviso.">
      <span>
        {label}{' '}
        <Chip size="small" variant="outlined" label="esperado" />
      </span>
    </Tooltip>
  );
};

/** "estava em X, a transação de -Y deixou em Z" — o que faz a linha ser log. */
const BalanceChangeField = (props: any) => {
  const record = useRecordContext();
  if (!record) { return null; }

  const format = (v: number) => Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (record.previousBalance === null || record.previousBalance === undefined) {
    return <span>{format(record.balance)}</span>;
  }

  return (
    <span>
      {format(record.previousBalance)} → <strong>{format(record.balance)}</strong>
    </span>
  );
};

const filters = [
  <SelectInput
    key="status"
    source="status"
    label="Situação"
    alwaysOn
    choices={[
      { id: 'open', name: 'Em aberto' },
      { id: 'resolved', name: 'Resolvidos' },
    ]}
  />,
  // perPage alto para trazer todas as redes de uma vez: sem isso o select vem
  // paginado e esconde redes. (O repo usa perPage={null} em outros lugares, mas
  // isso não passa no tipo do react-admin 5.)
  <ReferenceInput
    key="chainId"
    source="chainId"
    reference="chains"
    sort={{ field: 'name', order: 'ASC' }}
    perPage={1000}
    alwaysOn
  >
    <SelectInput resettable label="Rede" optionText="name" />
  </ReferenceInput>,
  <SelectInput
    key="caseType"
    source="caseType"
    label="Tipo de caso"
    alwaysOn
    choices={[
      { id: 'unexpected', name: 'Só os inesperados' },
      { id: 'all', name: 'Incluir estorno esperado' },
    ]}
  />,
  <SelectInput
    key="emailStatus"
    source="emailStatus"
    label="Notificação"
    choices={[
      { id: 'sent', name: 'Enviado' },
      { id: 'error', name: 'Falha no envio' },
    ]}
  />,
];

/**
 * `admin-profile` é o mesmo gate que o backend exige no `/admin/system-config`: quem só
 * tem `isAdmin` vê o relatório, mas não o botão.
 */
const ListActions = () => {
  const { permissions } = usePermissions();
  const [configOpen, setConfigOpen] = useState(false);

  const canConfigure = !!permissions && permissions.includes(PERMISSIONS.ADMIN_PROFILE);

  return (
    <TopToolbar>
      {/* Obrigatório: `actions` custom substitui o toolbar padrão, e sem isto o filtro
          "Notificação", que não é alwaysOn, fica inalcançável. */}
      <FilterButton />
      {canConfigure && (
        <FeatureIntro
          id="negative-balance-alert-config"
          title="Alertas de saldo negativo"
          description="Configure quem recebe o aviso das ocorrências de saldo negativo — por e-mail ou num canal do Discord — e desligue os envios a qualquer momento."
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Configurar alertas
          </Button>
        </FeatureIntro>
      )}
      {canConfigure && (
        <NegativeBalanceAlertConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />
      )}
    </TopToolbar>
  );
};

const NegativeBalanceAlertList = (props) => {
  return (
    <List
      {...props}
      title="Saldo negativo"
      resource="negative-balance-alerts"
      perPage={25}
      sort={{ field: 'detectedAt', order: 'DESC' }}
      filters={filters}
      filterDefaultValues={{ status: 'open', caseType: 'unexpected' }}
      pagination={<ListPagination />}
      actions={<ListActions />}
      exporter={false}
    >
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <CustomerWithLink label="CPF" source="customerCpf" />
        <ReferenceField
          label="Rede"
          source="chainId"
          reference="chains"
          textAlign="center"
        >
          <TextField source="name" />
        </ReferenceField>
        <BalanceChangeField label="Saldo (antes → depois)" textAlign="center" />
        <TransactionField label="Transação" textAlign="center" />
        <FunctionField
          label="Detecção"
          textAlign="center"
          render={(record) => (record.detectedBy === 'sweep' ? 'Varredura' : 'Tempo real')}
        />
        <DateField
          source="detectedAt"
          label="Detectado em"
          textAlign="center"
          locales="pt-BR"
          showTime
        />
        <StatusField label="Situação" textAlign="center" />
        <NotificationField label="Notificação" textAlign="center" />
        <DateField
          source="notifiedAt"
          label="Último aviso"
          textAlign="center"
          locales="pt-BR"
          showTime
          emptyText="—"
        />
      </Datagrid>
    </List>
  );
};

export default NegativeBalanceAlertList;
